import React, { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentUpdateRow, UpdateListDTO } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import BuscadorHorarios, { FiltroCasillas, HorarioFila } from "../../../../components/horarios/buscadorHorarios";
import S from "./strings";
import { DeshacerReparto, Fallo, auditoriaReparto, useEsCaja } from "./piezas";
import { Preset, TabProps, TYPE_LABELS, TYPE_ORDER, errorText, fechaCorta, rangoPreset, tipoDe } from "./helpers";
import { etiquetaDe } from "../../../../utils/etiquetaHorario";
import { primeraMayuscula } from "../../../../utils/textoPropio";
import PaginationComponent from "../../../../components/pagination/pagination";
import { buildPages } from "../enrollmentRequests/helpers";

/** "Sábados 3:00 pm — Octubre 2026 → Domingos 7:00 am — Noviembre 2026" y lo que explica el cambio (extra, nota). */
export const detalleDe = (r: EnrollmentUpdateRow): string => {
	const parts: string[] = [];
	const to = r.toCourse ? `${cursoCorto(r.toCourse.name) || r.toCourse.name}${r.toSchedule ? ", " + etiquetaDe(r.toSchedule) : ""}` : etiquetaDe(r.toSchedule);
	if (r.fromSchedule?.name || to) { parts.push(`${etiquetaDe(r.fromSchedule) || "—"}${to ? " → " + to : ""}`); }
	if (r.extraSessionsOption) { parts.push(S.extraOptions.find((o) => o.id === r.extraSessionsOption)?.t || ""); }
	if (r.statusId === 2 && r.returnReason) { parts.push(`${S.fixReason}: ${primeraMayuscula(r.returnReason)}`); }
	if (r.note) { const n = primeraMayuscula(r.note); parts.push(n.length > 90 ? n.slice(0, 90) + "…" : n); }
	return parts.filter(Boolean).join(". ");
};

/** Motivo de un rechazo: viene en la fila si el servidor lo manda; si no, se lee de los comentarios al mostrar la fila. */
const Motivo: React.FC<{ campusId: number; row: EnrollmentUpdateRow }> = ({ campusId, row }) => {
	const [texto, setTexto] = useState<string | null>(row.rejectReason ?? null);
	const [error, setError] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	useEffect(() => {
		if (texto !== null) { return; }
		let vigente = true;
		HttpManager.getInstance().enrollmentUpdatesService.details(campusId, row.id).then((r) => {
			if (!vigente) { return; }
			const c = [...(r.data.comments || [])].reverse().find((x) => /^(Descartada|Rechazada)/.test(x.text || ""));
			setTexto(c ? c.text.replace(/^(Descartada|Rechazada):?\s*/, "").replace(/\.$/, "") : "");
		}).catch((e) => vigente && setError(errorText(e)));
		return () => { vigente = false; };
	}, [row.id, intento]);
	if (texto !== null) { return <span className="wau-oro small">{S.rejection}: {texto || "—"}</span>; }
	return error ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => { setError(null); setIntento(intento + 1); }}>{S.seeReason}</button> : <span className="wau-sec small">{S.loading}</span>;
};

const clase = (r: EnrollmentUpdateRow): string => r.statusId === 3 ? "" : " wau-oro";
// Badge like Matrículas: pending = info (ink), returned / rejected = oro, applied and draft = neutral. Never green here.
const variante = (statusId: number): string => statusId === 1 ? "info" : statusId === 2 || statusId === 4 ? "oro" : "secondary";
const pill = (r: EnrollmentUpdateRow) => <Badge bg={variante(r.statusId)} style={{ fontWeight: 500 }}>{r.typeId === null && !r.typeLabel ? "—" : tipoDe(r)}</Badge>;
const cuando = (r: EnrollmentUpdateRow) => fechaCorta(r.eventAt || r.appliedAt || r.requestedAt);

/** Lo que tenía de descuento la matrícula vieja de un cambio de curso aplicado. */
const previo = (r: EnrollmentUpdateRow) => r.statusId === 3 && r.result?.previousAgreement?.text ? <div className="wau-sec small">{r.result.previousAgreement.text}</div> : null;

/** Lista de actualizaciones: tabla en pantalla grande, tarjetas en teléfono. */
export const HistorialLista: React.FC<{ campusId: number; countryId?: number | null; rows: EnrollmentUpdateRow[]; movil: boolean; onChanged?: (msg: string) => void; onError?: (msg: string) => void }> = ({ campusId, countryId, rows, movil, onChanged, onError }) => {
	const [deshechos, setDeshechos] = useState<number[]>([]);
	const esCaja = useEsCaja(campusId);
	const deshacer = (r: EnrollmentUpdateRow) => {
		const audit = auditoriaReparto(r);
		return esCaja && audit !== null && !deshechos.includes(audit) ? (
			<div style={{ marginTop: 4 }}><DeshacerReparto campusId={campusId} countryId={countryId} auditItemId={audit} studentName={r.studentName}
				onDone={(m) => { setDeshechos((d) => [...d, audit]); if (onChanged) { onChanged(m); } }} onError={(m) => { if (onError) { onError(m); } }} /></div>
		) : null;
	};
	if (rows.length === 0) { return <div className="wau-vacio">{S.empty}</div>; }
	if (movil) {
		return <div>{rows.map((r) => (
			<div className="wau-tarjeta" key={"l" + r.id}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><div><b>{r.studentName}</b>{r.courseName ? <div className="wau-sec small">{cursoCorto(r.courseName) || r.courseName}</div> : null}</div>{pill(r)}</div>
				<div className={"linea wau-num" + clase(r)}>{cuando(r)}, {S.statusLabels[r.statusId]}</div>
				<div style={{ fontSize: 13.5 }}>{detalleDe(r)}</div>
				{r.statusId === 4 ? <div style={{ marginTop: 4 }}><Motivo campusId={campusId} row={r} /></div> : null}
				{previo(r)}
				{deshacer(r)}
				<div className="linea">{r.requestedBy || "—"}{r.appliedBy ? ", " + r.appliedBy : ""}</div>
			</div>
		))}</div>;
	}
	return (
		<div className="wau-tabla">
			<table>
				<thead><tr><th>{S.col.date}</th><th>{S.col.student}</th><th>{S.col.update}</th><th>{S.col.detail}</th><th>{S.col.requestedBy}</th><th>{S.col.appliedBy}</th></tr></thead>
				<tbody className="wau-num">
					{rows.map((r) => (
						<tr key={"l" + r.id}>
							<td style={{ whiteSpace: "nowrap" }}>{cuando(r)}</td>
							<td><b>{r.studentName}</b>{r.courseName ? <div className="wau-sec small">{cursoCorto(r.courseName) || r.courseName}</div> : null}</td>
							<td style={{ whiteSpace: "nowrap" }}>{pill(r)}<div className={"small" + (r.statusId === 3 ? " wau-sec" : " wau-oro")}>{S.statusLabels[r.statusId]}</div></td>
							<td>{detalleDe(r)}{r.statusId === 4 ? <div><Motivo campusId={campusId} row={r} /></div> : null}{previo(r)}{deshacer(r)}</td>
							<td>{r.requestedBy || "—"}</td>
							<td>{r.appliedBy || "—"}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const ESTADOS = [1, 2, 3, 4];

/** Historial: todo lo pedido, aplicado y rechazado, con filtros de fecha, tipo, estado, quién y horario. */
const HistorialTab: React.FC<TabProps> = ({ campusId, countryId, movil, notify, changed }) => {
	const http = HttpManager.getInstance();
	const [preset, setPreset] = useState<Preset>("semana");
	const [tipo, setTipo] = useState<string[]>([]);
	const [estado, setEstado] = useState<string[]>([]);
	const [pidio, setPidio] = useState<string[]>([]);
	const [aplico, setAplico] = useState<string[]>([]);
	const [horario, setHorario] = useState<HorarioFila | null>(null);
	const [eligiendo, setEligiendo] = useState<boolean>(false);
	const [q, setQ] = useState<string>("");
	// La búsqueda va al servidor (todo el historial del plantel, no solo la página cargada), con una pausa al escribir.
	const [buscar, setBuscar] = useState<string>("");
	const [page, setPage] = useState<number>(1);
	const [result, setResult] = useState<UpdateListDTO | null>(null);
	const [fallo, setFallo] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	// Quién pidió / aplicó llega solo con la página 1: se guarda para las demás.
	const [gente, setGente] = useState<{ requesters: { id: number; name: string }[]; appliers: { id: number; name: string }[] }>({ requesters: [], appliers: [] });

	useEffect(() => {
		const t = setTimeout(() => { const v = q.trim(); if (v !== buscar) { setBuscar(v); setPage(1); } }, 350);
		return () => clearTimeout(t);
	}, [q]);

	useEffect(() => {
		let vigente = true;
		setFallo(null);
		// Buscando, se busca en todo el historial (sin el rango de fechas).
		http.enrollmentUpdatesService.log(campusId, { ...(buscar ? {} : rangoPreset(preset)), typeId: Number(tipo[0]) || null, statusId: estado.length ? estado.join(",") : null,
			requestedBy: Number(pidio[0]) || null, appliedBy: Number(aplico[0]) || null, scheduleId: horario?.id ?? null, page, ...(buscar ? { search: buscar } : {}) } as any)
			.then((r) => {
				if (!vigente) { return; }
				setResult(r.data);
				if (r.data.requesters || r.data.appliers) { setGente({ requesters: r.data.requesters || [], appliers: r.data.appliers || [] }); }
			}).catch((e) => vigente && setFallo(errorText(e)));
		return () => { vigente = false; };
	}, [campusId, preset, tipo, estado, pidio, aplico, horario, page, buscar, intento]);

	const cambia = <V,>(set: (v: V) => void) => (v: V) => { set(v); setPage(1); };
	const rows = result?.data || [];
	const hayFiltros = tipo.length + estado.length + pidio.length + aplico.length > 0 || !!horario;
	const quitarFiltros = () => { setTipo([]); setEstado([]); setPidio([]); setAplico([]); setHorario(null); setPage(1); };
	// Matrículas' pager. The server sends 100 per page (fixed), so that is the only size offered.
	const porPagina = result?.perPage || 100;
	const pages = result ? buildPages(result.currentPage || page, result.lastPage, (p) => setPage(p), movil) : [];
	const itemCount = result ? { start: result.total === 0 ? 0 : (result.currentPage - 1) * porPagina + 1, end: Math.min(result.currentPage * porPagina, result.total), total: result.total } : { start: 0, end: 0, total: 0 };

	const presets: Preset[] = ["hoy", "semana", "mes", "todo"];
	return (
		<div className="wau-pila">
			<div className="wau-fila" style={{ alignItems: "center" }}>
				<div className="waFmSegs" role="group">
					{presets.map((p) => { const on = buscar ? p === "todo" : preset === p; return <button key={p} type="button" className={"waFmSeg" + (on ? " activo" : "")} aria-pressed={on} onClick={() => { setPreset(p); setPage(1); setQ(""); setBuscar(""); }}>{S.presets[p]}</button>; })}
				</div>
				<div className="wau-buscar grow" style={{ maxWidth: movil ? undefined : 360, marginLeft: movil ? 0 : "auto" }}>
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
					<input value={q} placeholder={S.searchLog} aria-label={S.searchLog} onChange={(e) => setQ(e.target.value)} />
				</div>
			</div>
			<div className="waFmBarra wau-barra">
				<div className="waFmBarraListas">
					<FiltroCasillas unico etiqueta={S.filterType} opciones={TYPE_ORDER.map((id) => ({ v: String(id), t: TYPE_LABELS[id] }))} elegidos={tipo} onCambio={cambia(setTipo)} />
					<FiltroCasillas etiqueta={S.statusFilter} opciones={ESTADOS.map((id) => ({ v: String(id), t: S.statusLabels[id] }))} elegidos={estado} onCambio={cambia(setEstado)} />
					<FiltroCasillas unico etiqueta={S.filterRequester} opciones={gente.requesters.map((p) => ({ v: String(p.id), t: p.name }))} elegidos={pidio} onCambio={cambia(setPidio)} />
					<FiltroCasillas unico etiqueta={S.appliedByFilter} opciones={gente.appliers.map((p) => ({ v: String(p.id), t: p.name }))} elegidos={aplico} onCambio={cambia(setAplico)} />
					<span className="wbh-filtro">
						<button type="button" className={"waFmBoton" + (horario ? " activo" : "")} onClick={() => setEligiendo(true)}>{S.filterSchedule}{horario ? <span className="waFmValor">{horario.horario}{horario.periodo ? ", " + horario.periodo : ""}</span> : <span className="waFmFlecha" aria-hidden="true">▾</span>}</button>
						{horario ? <button type="button" className="wbh-x" aria-label={"Quitar " + S.filterSchedule} onClick={() => { setHorario(null); setPage(1); }}>×</button> : null}
					</span>
					{hayFiltros ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={quitarFiltros}>{S.removeFilters}</button> : null}
				</div>
			</div>
			{fallo ? <Fallo texto={fallo} onReintentar={() => setIntento((n) => n + 1)} /> : result === null ? <div className="wau-caja wau-sec">{S.loading}</div> : <HistorialLista campusId={campusId} countryId={countryId} rows={rows} movil={movil} onChanged={(m) => { notify(m); changed(); }} onError={(m) => notify(m, false)} />}
			{result && !fallo && result.total > 0 ? (
				<div className="wau-paginas">
					<PaginationComponent pages={pages} pageSizes={[porPagina]} itemCount={itemCount} onPageSizeChange={() => setPage(1)} />
				</div>
			) : null}
			{eligiendo ? (
				<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={() => setEligiendo(false)}>
					<div className="wau-modal ancho" onClick={(e) => e.stopPropagation()}>
						<h3>{S.filterSchedule}</h3>
						<BuscadorHorarios campusId={campusId} autoFocus alcance="historial" seleccionado={horario?.id} onElegir={(h) => { setHorario(h); setPage(1); setEligiendo(false); }} />
						<div className="wau-acciones"><button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEligiendo(false)}>{S.cancel}</button></div>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default HistorialTab;
